import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { db } from '../Firebase';
import { collection, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FolderOpen, Trash2, X, Loader2, 
  Search, Phone, Mail, Clock, MessageSquare, Hospital, ShieldCheck,
  Package
} from 'lucide-react';

export default function ProductEnquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal viewer
  const [selectedInq, setSelectedInq] = useState(null);

  // Search/Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProduct, setFilterProduct] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All'); // 'All', 'new', 'completed'

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const snap = await getDocs(collection(db, 'productEnquiries'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort by submittedAt desc
      list.sort((a, b) => {
        const timeA = a.submittedAt?.seconds || 0;
        const timeB = b.submittedAt?.seconds || 0;
        return timeB - timeA;
      });
      setInquiries(list);
    } catch (err) {
      console.error("Error loading product enquiries:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to load product enquiries log.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, []);

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      setLoading(true);
      const docRef = doc(db, 'productEnquiries', id);
      await updateDoc(docRef, { status: newStatus });
      setInquiries(inquiries.map(inq => inq.id === id ? { ...inq, status: newStatus } : inq));
      if (selectedInq?.id === id) {
        setSelectedInq({ ...selectedInq, status: newStatus });
      }
      Swal.fire({
        title: 'Success',
        text: `Enquiry marked as ${newStatus}!`,
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error updating status:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to update status.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#111111',
      cancelButtonColor: '#DE2929',
      confirmButtonText: 'Yes, delete it!'
    });

    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      await deleteDoc(doc(db, 'productEnquiries', id));
      setInquiries(inquiries.filter(inq => inq.id !== id));
      setSelectedInq(null);
      Swal.fire({
        title: 'Success',
        text: 'Enquiry log deleted successfully!',
        icon: 'success',
        confirmButtonColor: '#111111'
      });
    } catch (err) {
      console.error("Error deleting product enquiry:", err);
      Swal.fire({
        title: 'Error',
        text: 'Failed to delete enquiry.',
        icon: 'error',
        confirmButtonColor: '#111111'
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredInqs = inquiries.filter(inq => {
    const matchesSearch = (inq.contactName || inq.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (inq.hospitalName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (inq.productName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (inq.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = filterProduct === 'All' || inq.productName === filterProduct;
    const status = inq.status || 'new';
    const matchesStatus = filterStatus === 'All' || status === filterStatus;
    return matchesSearch && matchesProduct && matchesStatus;
  });

  // Extract unique products for filtering
  const productsList = ['All', ...new Set(inquiries.map(inq => inq.productName).filter(Boolean))];

  return (
    <div className="space-y-8 font-poppins">
      
      {/* Header */}
      <div className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-silver/50 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-extrabold font-montserrat text-brand-navy">Product Enquiries Log</h1>
          <p className="text-xs text-gray-500">Track and review pricing quotation requests, product quantities, and hospital specifications.</p>
        </div>
      </div>



      {/* Search & Filtering Toolbar */}
      <div className="bg-white p-4 rounded-2xl border border-brand-silver/50 shadow-sm flex flex-col lg:flex-row gap-4 items-center justify-between">
        {/* Search */}
        <div className="relative w-full lg:max-w-md">
          <input
            type="text"
            placeholder="Search by product, hospital, contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins"
          />
          <Search size={14} className="absolute left-3.5 top-3.5 text-gray-400" />
        </div>

        {/* Filters Group */}
        <div className="w-full lg:w-auto flex flex-col sm:flex-row gap-3">
          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full sm:w-44 px-3 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
          >
            <option value="All">All Statuses</option>
            <option value="new">New Enquiries</option>
            <option value="completed">Completed Enquiries</option>
          </select>

          {/* Products Filter */}
          <select
            value={filterProduct}
            onChange={(e) => setFilterProduct(e.target.value)}
            className="w-full sm:w-56 px-3 py-2.5 border border-brand-silver rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-xs font-poppins bg-white"
          >
            {productsList.map(prod => (
              <option key={prod} value={prod}>{prod === 'All' ? 'All Products' : prod}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Log list grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-gray-400 space-y-4">
          <Loader2 className="animate-spin text-brand-blue" size={36} />
          <p className="text-xs font-bold text-gray-500">Retrieving product enquiries history...</p>
        </div>
      ) : filteredInqs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-brand-silver/50 p-12 text-center text-gray-400 max-w-md mx-auto space-y-4">
          <FolderOpen size={48} className="mx-auto text-gray-300" />
          <h3 className="text-lg font-bold font-montserrat text-brand-navy">No Enquiries Found</h3>
          <p className="text-xs font-light text-gray-500 font-poppins">No new product enquiries matching search parameters were found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredInqs.map((inq) => (
            <motion.div
              key={inq.id}
              layout
              className="bg-white p-6 rounded-2xl border border-brand-silver/60 shadow-sm flex flex-col justify-between hover:shadow-md transition-all group"
            >
              <div className="space-y-4">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <span className="bg-brand-green/10 text-brand-green text-[9px] font-extrabold uppercase tracking-widest px-2.5 py-0.5 rounded flex items-center gap-1 w-max">
                      <Package size={10} /> {inq.productName}
                    </span>
                    <h3 className="text-sm font-extrabold font-montserrat text-brand-navy mt-1.5">{inq.hospitalName || 'Individual/Other'}</h3>
                    <p className="text-[10px] text-gray-400 font-semibold font-poppins">Logged: {inq.submittedAt ? new Date(inq.submittedAt.seconds * 1000).toLocaleString() : 'Just now'}</p>
                  </div>

                  {/* Badges/Status */}
                  <div className="flex gap-2 items-center shrink-0">
                    <span className={`text-[8px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${
                      inq.status === 'completed' 
                        ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' 
                        : 'bg-amber-500/10 text-amber-600 border border-amber-500/20'
                    }`}>
                      {inq.status || 'new'}
                    </span>
                    <button 
                      onClick={() => handleDelete(inq.id)}
                      className="p-1 rounded border border-brand-silver text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors opacity-40 group-hover:opacity-100"
                      title="Delete log"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <p className="text-xs font-bold text-brand-navy font-poppins">
                    {inq.contactName || inq.name} — <span className="text-gray-400">Qty: {inq.quantity || 1}</span>
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed font-light line-clamp-2 italic">
                    "{inq.notes || 'No additional specifications specified.'}"
                  </p>
                </div>

                {/* Footer specs */}
                <div className="flex flex-wrap gap-3 text-[10px] text-gray-400 font-semibold font-poppins pt-3 border-t border-brand-silver/50 justify-between items-center">
                  <div className="flex gap-3">
                    <span className="flex items-center gap-1"><Phone size={10} /> {inq.phone}</span>
                    {inq.email && <span className="flex items-center gap-1"><Mail size={10} /> {inq.email}</span>}
                  </div>
                  
                  <button 
                    onClick={() => setSelectedInq(inq)}
                    className="text-xs font-bold text-brand-blue hover:text-brand-navy flex items-center gap-0.5"
                  >
                    View Details
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Detailed Inquiry Inspection Drawer */}
      <AnimatePresence>
        {selectedInq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedInq(null)}
              className="absolute inset-0 bg-black"
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="bg-white w-full max-w-xl rounded-3xl overflow-hidden shadow-2xl border border-brand-silver/50 relative z-10 flex flex-col"
            >
              {/* Header */}
              <div className="p-6 border-b border-brand-silver/50 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold font-montserrat text-brand-navy">Detailed Product Enquiry Log</h2>
                  <p className="text-xs text-gray-500">Logged client specs and contact coordinates.</p>
                </div>
                <button 
                  onClick={() => setSelectedInq(null)}
                  className="p-1.5 rounded-lg hover:bg-brand-light text-gray-400 hover:text-brand-navy transition-all"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Details Body */}
              <div className="p-6 space-y-6 overflow-y-auto max-h-[70vh]">
                
                {/* Upper Name & Position */}
                <div className="flex gap-4 p-5 rounded-2xl bg-brand-light border border-brand-silver/60">
                  <div className="w-12 h-12 rounded-xl bg-brand-green/15 text-brand-green flex items-center justify-center font-bold text-lg shrink-0">
                    <Package size={22} />
                  </div>
                  <div>
                    <h3 className="text-base font-extrabold font-montserrat text-brand-navy">{selectedInq.productName}</h3>
                    <p className="text-xs text-brand-blue font-bold font-poppins uppercase tracking-wider mt-0.5">
                      Quantity Requested: {selectedInq.quantity || 1}
                    </p>
                    <span className="text-[10px] text-gray-400 font-poppins flex items-center gap-1 mt-1">
                      <Clock size={12} /> Logged: {selectedInq.submittedAt ? new Date(selectedInq.submittedAt.seconds * 1000).toLocaleString() : 'Just now'}
                    </span>
                  </div>
                </div>

                {/* Candidate Stats Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Authorized Signatory / Contact</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins">{selectedInq.contactName || selectedInq.name}</span>
                  </div>
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Hospital / Institution</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><Hospital size={12} className="text-gray-400" /> {selectedInq.hospitalName || 'Individual / Other'}</span>
                  </div>
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Phone Contacts</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><Phone size={12} className="text-gray-400" /> {selectedInq.phone}</span>
                  </div>
                  <div className="bg-white border border-brand-silver p-4 rounded-xl space-y-1">
                    <span className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider block">Email Address</span>
                    <span className="text-xs font-bold text-brand-navy font-poppins flex items-center gap-1.5"><Mail size={12} className="text-gray-400" /> {selectedInq.email || 'No email provided'}</span>
                  </div>
                </div>

                {/* Project Notes */}
                <div className="space-y-1.5">
                  <label className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider">Specifications & Notes</label>
                  <div className="p-4 rounded-xl bg-brand-light border border-brand-silver text-xs text-gray-600 leading-relaxed font-poppins whitespace-pre-wrap flex gap-2.5">
                    <MessageSquare size={16} className="text-gray-400 shrink-0 mt-0.5" />
                    <span>{selectedInq.notes || 'No additional notes provided.'}</span>
                  </div>
                </div>

                {/* Toggle Status Actions */}
                <div className="space-y-2 border-t border-brand-silver/50 pt-4">
                  <label className="text-[9px] uppercase font-bold text-gray-400 font-poppins tracking-wider">Inquiry Resolution Status</label>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleUpdateStatus(selectedInq.id, 'new')}
                      disabled={selectedInq.status === 'new' || !selectedInq.status}
                      className="px-4 py-2 border border-brand-silver rounded-xl text-xs font-bold font-poppins hover:bg-brand-light disabled:opacity-40"
                    >
                      Mark as New
                    </button>
                    <button 
                      onClick={() => handleUpdateStatus(selectedInq.id, 'completed')}
                      disabled={selectedInq.status === 'completed'}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold font-poppins flex items-center gap-1.5 disabled:opacity-40"
                    >
                      <ShieldCheck size={14} /> Mark as Contacted
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="p-6 border-t border-brand-silver/50 flex justify-between">
                <button 
                  onClick={() => handleDelete(selectedInq.id)}
                  className="px-4 py-2 border border-red-200 hover:bg-red-50 text-red-600 text-xs font-bold rounded-xl transition-all"
                >
                  Delete Log Entry
                </button>
                <button 
                  onClick={() => setSelectedInq(null)}
                  className="px-5 py-2.5 border border-brand-silver hover:bg-brand-light text-brand-navy text-xs font-bold rounded-xl transition-all"
                >
                  Close Inquiry
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
